(function () {
  if (window.CartApi && window.configureCartApi) return;

  class CartApi {
    constructor(options = {}) {
      this.type = this.getDefaultCartType();
      this.options = {};
      this.skipNextCartCallback = false;
      this.lastCartSignature = null;
      this.expectedInternalCartSignature = null;
      this.hasCartReadyEventFired = false;
      this.hasHandledCartReady = false;
      this.cartReadyCart = null;
      this.cartReadyOptions = null;
      this.cartReadyPromise = null;
      this.cartReadyCallbacks = [];
      this.cartOpenCallbacks = [];
      this.cartItemsMutateCallbacks = [];
      this.cartItemsObserveCallbacks = [];
      this.upcartCartUpdatedUnsubscribe = null;
      this.upcartCartOpenedUnsubscribe = null;
      this.upcartCartUpdatedTimer = null;

      this.configure(options);
      this.bindDeferredUpCartEvents();
      this.bindCartReadyEvent();
    }

    configure(options = {}) {
      this.options = {
        ...this.options,
        ...options,
      };

      if (options.type) {
        this.type = options.type;
      }

      this.bindUpCartEvents();
      return this;
    }

    getDefaultCartType() {
      return this.getShopDomain() === "dreame-us.myshopify.com" ? "upcart" : "timber";
    }

    getShopDomain() {
      const shop = window.shop || window.Shopify?.shop || "";

      return String(shop)
        .trim()
        .replace(/^['"]+|['"]+$/g, "")
        .toLowerCase();
    }

    bindDeferredUpCartEvents() {
      window.addEventListener("DOMContentLoaded", () => this.bindUpCartEvents());
      window.addEventListener("load", () => this.bindUpCartEvents());
    }

    bindCartReadyEvent() {
      const markCartReady = () => {
        this.hasCartReadyEventFired = true;

        if (this.cartReadyCallbacks.length === 0) return;

        this.handleCartReady({
          source: "page_load",
          refresh: false,
        }).catch((error) => {
          console.error("Cart API ready callback failed:", error);
        });
      };

      if (document.readyState === "complete") {
        setTimeout(markCartReady, 0);
        return;
      }

      window.addEventListener("load", markCartReady, { once: true });
    }

    shouldBindUpCartEvents() {
      return this.type === "upcart";
    }

    bindUpCartEvents() {
      if (!this.shouldBindUpCartEvents()) {
        this.unbindUpCartEvents();
        return;
      }

      if (
        !this.upcartCartUpdatedUnsubscribe &&
        window.upcartSubscribeCartUpdated &&
        typeof window.upcartSubscribeCartUpdated === "function" &&
        !this.isEmptyFunction(window.upcartSubscribeCartUpdated)
      ) {
        this.upcartCartUpdatedUnsubscribe = window.upcartSubscribeCartUpdated(({ cart } = {}) => {
          this.scheduleCartItemsUpdated(cart, { source: "upcart_cart_updated" });
        });
      }

      if (
        !this.upcartCartOpenedUnsubscribe &&
        window.upcartSubscribeCartOpened &&
        typeof window.upcartSubscribeCartOpened === "function" &&
        !this.isEmptyFunction(window.upcartSubscribeCartOpened)
      ) {
        this.upcartCartOpenedUnsubscribe = window.upcartSubscribeCartOpened(() => {
          this.handleCartOpen({
            source: "upcart_cart_opened",
            refresh: false,
          }).catch((error) => {
            console.error("Cart API open callback failed:", error);
          });
        });
      }
    }

    unbindUpCartEvents() {
      if (typeof this.upcartCartUpdatedUnsubscribe === "function") {
        this.upcartCartUpdatedUnsubscribe();
      }
      if (typeof this.upcartCartOpenedUnsubscribe === "function") {
        this.upcartCartOpenedUnsubscribe();
      }

      this.upcartCartUpdatedUnsubscribe = null;
      this.upcartCartOpenedUnsubscribe = null;
    }

    addCallback(callbacks, callback) {
      if (typeof callback !== "function") return () => {};

      callbacks.push(callback);
      return () => {
        const index = callbacks.indexOf(callback);
        if (index >= 0) callbacks.splice(index, 1);
      };
    }

    onCartOpen(callback) {
      return this.addCallback(this.cartOpenCallbacks, callback);
    }

    onCartReady(callback) {
      if (typeof callback !== "function") return () => {};

      this.cartReadyCallbacks.push(callback);

      if (this.hasHandledCartReady && this.isValidCart(this.cartReadyCart)) {
        setTimeout(() => {
          callback({
            cart: this.cartReadyCart,
            options: this.cartReadyOptions || {},
          });
        }, 0);
      } else if (this.hasCartReadyEventFired) {
        this.handleCartReady({
          source: "page_load",
          refresh: false,
        }).catch((error) => {
          console.error("Cart API ready callback failed:", error);
        });
      }

      return () => {
        this.cartReadyCallbacks = this.cartReadyCallbacks.filter((entry) => entry !== callback);
      };
    }

    onCartItemsMutate(callback) {
      return this.addCallback(this.cartItemsMutateCallbacks, callback);
    }

    onCartItemsObserve(callback) {
      return this.addCallback(this.cartItemsObserveCallbacks, callback);
    }

    async handleCartOpen(options = {}) {
      let cart = null;

      if (this.cartOpenCallbacks.length > 0) {
        cart = await this.getCart(options);
        this.rememberCart(cart);

        for (const callback of this.cartOpenCallbacks) {
          await callback({ cart, options });
        }
      }

      if (options.refresh !== false) {
        await this.refreshCart(options);
      }

      return cart;
    }

    async handleCartReady(options = {}) {
      if (this.cartReadyPromise) return this.cartReadyPromise;
      if (this.hasHandledCartReady) return this.cartReadyCart;
      if (this.cartReadyCallbacks.length === 0) return null;

      this.cartReadyOptions = options;
      this.cartReadyPromise = (async () => {
        const cart = await this.getCart({
          ...options,
          forceFetch: true,
        });
        this.rememberCart(cart);
        this.cartReadyCart = cart;
        this.hasHandledCartReady = true;

        for (const callback of this.cartReadyCallbacks) {
          await callback({ cart, options });
        }

        return cart;
      })();

      try {
        return await this.cartReadyPromise;
      } finally {
        this.cartReadyPromise = null;
      }
    }

    notifyTimberCartUpdated(cart, options = {}) {
      if (this.type !== "timber") return Promise.resolve(null);

      return this.handleCartItemsUpdated(cart, {
        ...options,
        source: options.source || "timber_cart_updated",
      });
    }

    notifyTimberCartOpened(options = {}) {
      if (this.type !== "timber") return Promise.resolve(null);

      return this.handleCartOpen({
        ...options,
        source: options.source || "timber_cart_opened",
      });
    }

    scheduleCartItemsUpdated(cart, options = {}) {
      if (this.upcartCartUpdatedTimer) {
        clearTimeout(this.upcartCartUpdatedTimer);
      }

      this.upcartCartUpdatedTimer = setTimeout(async () => {
        this.upcartCartUpdatedTimer = null;

        try {
          await this.handleCartItemsUpdated(cart, options);
        } catch (error) {
          console.error("Cart API items change callback failed:", error);
        }
      }, 100);
    }

    async handleCartItemsUpdated(cart, options = {}) {
      const normalizedCart = this.normalizeCart(cart);
      if (!this.hasCartChanged(normalizedCart)) return null;

      let currentCart = normalizedCart;

      for (const callback of this.cartItemsMutateCallbacks) {
        const result = await callback({ cart: currentCart, options });
        if (this.isValidCart(result)) {
          currentCart = this.normalizeCart(result);
        }
      }

      this.rememberCart(currentCart);

      for (const callback of this.cartItemsObserveCallbacks) {
        await callback({ cart: currentCart, options });
      }

      return currentCart;
    }

    async getCart(options = {}) {
      if (!options.forceFetch && this.isValidCart(options.cart)) {
        return this.normalizeCart(options.cart);
      }

      const cartRes = await fetch("/cart.js?_c_=t");
      if (!cartRes.ok) {
        throw new Error(`Cart API failed to fetch cart: ${cartRes.status}`);
      }

      const r = await cartRes.json()
      return this.normalizeCart(r);
    }

    async addItems(items, options = {}) {
      if (!Array.isArray(items) || items.length === 0) return null;

      return this.withSuppressedUpCartAutoOpen(async () => {
        const addRes = await fetch("/cart/add.js?_c_=t", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items }),
        });

        if (!addRes.ok) {
          throw new Error(`Cart API add failed: ${addRes.status}`);
        }

        const cart = await this.getCart({ forceFetch: true });
        return this.handleCartMutationResult(cart, {
          ...options,
          source: options.source || "cart_api_add_items",
        });
      });
    }

    async updateItems(updates, options = {}) {
      if (!Array.isArray(updates) || updates.length === 0) return null;

      const formData = new FormData();
      updates.forEach((update) => {
        const target = update.key || update.id;
        if (!target && target !== 0) return;
        formData.append(`updates[${target}]`, Number(update.quantity || 0));
      });

      if (formData.entries().next().done) return null;

      const updateRes = await fetch("/cart/update.js?_c_=t", {
        method: "POST",
        body: formData,
      });

      if (!updateRes.ok) {
        throw new Error(`Cart API update failed: ${updateRes.status}`);
      }

      const cart = this.normalizeCart(await updateRes.json());
      return this.handleCartMutationResult(cart, {
        ...options,
        source: options.source || "cart_api_update_items",
      });
    }

    async handleCartMutationResult(cart, options = {}) {
      if (options.notify === false) {
        this.rememberInternalCartMutation(cart);
        return cart;
      }

      return this.handleCartItemsUpdated(cart, options);
    }

    async refreshCart(options = {}) {
      if (typeof this.options.refreshCart === "function") {
        return this.options.refreshCart(options);
      }

      if (this.type === "timber") {
        return this.refreshTimberCart();
      }

      if (this.type === "upcart") {
        return this.refreshUpCart();
      }

      if (await this.refreshUpCart()) return;
      return this.refreshTimberCart();
    }

    async refreshUpCart() {
      if (
        window.upcartRefreshCart &&
        typeof window.upcartRefreshCart === "function" &&
        !this.isEmptyFunction(window.upcartRefreshCart)
      ) {
        await window.upcartRefreshCart();
        return true;
      }

      return false;
    }

    refreshTimberCart() {
      if (
        window.ajaxCart?.load &&
        typeof window.ajaxCart.load === "function" &&
        !this.isEmptyFunction(window.ajaxCart.load)
      ) {
        this.markNextCartCallbackHandled();
        window.ajaxCart.load();
        return true;
      }

      return false;
    }

    markNextCartCallbackHandled() {
      this.skipNextCartCallback = true;
    }

    consumeNextCartCallbackHandled() {
      if (!this.skipNextCartCallback) return false;
      this.skipNextCartCallback = false;
      return true;
    }

    rememberCart(cart) {
      if (!this.isValidCart(cart)) return null;
      this.lastCartSignature = this.getCartSignature(cart);
      return this.lastCartSignature;
    }

    rememberInternalCartMutation(cart) {
      if (!this.isValidCart(cart)) return null;

      const signature = this.getCartSignature(cart);
      this.expectedInternalCartSignature = signature;
      this.lastCartSignature = signature;
      return signature;
    }

    hasCartChanged(cart) {
      if (!this.isValidCart(cart)) return false;

      const signature = this.getCartSignature(cart);

      if (signature === this.expectedInternalCartSignature) {
        this.expectedInternalCartSignature = null;
        this.lastCartSignature = signature;
        return false;
      }

      this.expectedInternalCartSignature = null;

      if (signature === this.lastCartSignature) return false;

      this.lastCartSignature = signature;
      return true;
    }

    getCartSignature(cart) {
      if (!this.isValidCart(cart)) return "";

      return cart.items
        .map((item) => {
          const properties = this.getComparableProperties(item.properties || {});
          const sellingPlanId =
            item.selling_plan_allocation?.selling_plan?.id || item.sellingPlanAllocation?.sellingPlan?.id || "";

          return {
            key: item.key || "",
            variantId: item.variant_id || item.variantId || item.id || "",
            quantity: Number(item.quantity || 0),
            sellingPlanId,
            isGift: this.isGiftItem(item),
            properties,
          };
        })
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
        .map((item) => JSON.stringify(item))
        .join("|");
    }

    getComparableProperties(properties) {
      return Object.keys(properties)
        .filter((key) => {
          return ![
            "__dreame_gift",
            "__dreame_gift_source",
            "__first_n_orders_gift",
            "__first_n_orders_gift_handle",
            "__first_n_orders_gift_rule_index",
          ].includes(key);
        })
        .sort()
        .reduce((result, key) => {
          result[key] = properties[key];
          return result;
        }, {});
    }

    isGiftItem(item) {
      return !!(
        item?.isRewardItem ||
        item?.properties?.__dreame_gift ||
        item?.properties?.__dreame_gift_source ||
        item?.properties?.__first_n_orders_gift
      );
    }

    normalizeCart(cart) {
      if (!this.isValidCart(cart)) return cart;

      return {
        ...cart,
        total_price: cart.total_price ?? cart.total ?? 0,
        original_total_price:
          cart.original_total_price ?? cart.totalBeforeDiscounts ?? cart.total_price ?? cart.total ?? 0,
        items_subtotal_price: cart.items_subtotal_price ?? cart.total ?? cart.total_price ?? 0,
        currency: cart.currency,
        item_count: cart.item_count ?? cart.items.reduce((count, item) => count + Number(item.quantity || 0), 0),
        items: cart.items.map((item) => ({
          ...item,
          product_id: item.product_id ?? item.productId,
          variant_id: item.variant_id ?? item.variantId ?? item.id,
          product_type: item.product_type ?? item.productType,
          final_line_price: item.final_line_price ?? item.finalLinePrice ?? 0,
          final_price: item.final_price ?? item.finalPrice ?? 0,
          original_line_price: item.original_line_price ?? item.originalLinePrice ?? 0,
          original_price: item.original_price ?? item.originalPrice ?? 0,
          selling_plan_allocation: item.selling_plan_allocation ?? item.sellingPlanAllocation,
        })),
      };
    }

    isValidCart(cart) {
      return !!cart && Array.isArray(cart.items);
    }

    async withSuppressedUpCartAutoOpen(task) {
      const key = "upcartShouldOpenOnAddToCartBackgroundNetworkCall";
      const hadOriginalValue = Object.prototype.hasOwnProperty.call(window, key);
      const originalValue = window[key];

      window[key] = false;

      try {
        return await task();
      } finally {
        setTimeout(() => {
          if (hadOriginalValue) {
            window[key] = originalValue;
          } else {
            delete window[key];
          }
        }, 1000);
      }
    }

    isEmptyFunction(fn) {
      if (typeof fn !== "function") return true;

      const str = fn.toString().replace(/\s/g, "");
      return str === "async()=>{}" || str === "()=>{}" || str === "function(){}";
    }
  }

  window.CartApi = new CartApi();
  window.configureCartApi = function (options) {
    return window.CartApi.configure(options || {});
  };
})();
